"use client";

import Link from "next/link";
import { useSession } from "next-auth/react";

export default function LandingPage() {
  const { data: session } = useSession();

  return (
    <main className="min-h-screen bg-[#050505] text-slate-50 font-sans overflow-x-hidden selection:bg-emerald-500/30">
      
      {/* NUMERAI / TWO SIGMA - Global Cyberpunk Background Orbs */}
      <div className="fixed top-[-20%] left-[-10%] w-[800px] h-[800px] rounded-full bg-blue-900/20 blur-[150px] pointer-events-none -z-10 animate-[pulse_8s_ease-in-out_infinite]" />
      <div className="fixed bottom-[-20%] right-[-10%] w-[800px] h-[800px] rounded-full bg-emerald-900/10 blur-[150px] pointer-events-none -z-10 animate-[pulse_10s_ease-in-out_infinite]" />

      {/* HERO SECTION (Two Sigma vibe: "We use math to win") */}
      <section className="relative pt-24 pb-32 px-6 max-w-7xl mx-auto flex flex-col items-center text-center">
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-emerald-500/30 bg-emerald-500/10 backdrop-blur-md mb-8 animate-[fade-in_1s_ease-out]">
          <span className="w-2 h-2 rounded-full bg-emerald-400 shadow-[0_0_10px_rgba(52,211,153,0.8)] animate-pulse" />
          <span className="text-xs font-bold text-emerald-300 tracking-widest uppercase">Live: 6 Strategies Evaluating the S&P500</span>
        </div>
        
        <h1 className="text-6xl md:text-8xl font-black tracking-tighter mb-8 leading-[1.1] max-w-5xl">
          Institutional alpha.<br/>
          <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 via-indigo-400 to-emerald-400">
            Powered by pure math.
          </span>
        </h1>
        
        <p className="text-xl md:text-2xl text-slate-400 max-w-3xl mb-12 leading-relaxed font-light">
          Stratos is a quantitative algorithmic execution engine. We remove human emotion and deploy complex machine logic to extract asymmetrical returns.
        </p>
        
        <div className="flex flex-col sm:flex-row gap-6">
          <Link href={session ? "/dashboard" : "/register"} className="px-10 py-4 rounded-full bg-blue-600 hover:bg-blue-500 text-white font-bold text-lg shadow-[0_0_40px_-5px_rgba(37,99,235,0.5)] transition-all duration-300 hover:scale-105">
            {session ? "Access Live Analytics" : "Subscribe to Unlock Live Data"}
          </Link>
        </div>
      </section>

      {/* COLLECTIVE2 TEASER - Performance Metrics Grid */}
      <section className="py-20 relative z-10 max-w-6xl mx-auto px-6">
        <div className="w-full rounded-3xl bg-slate-900/40 backdrop-blur-2xl border border-white/10 p-8 md:p-12 shadow-2xl relative overflow-hidden group">
          <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-blue-500 via-indigo-500 to-emerald-500" />
          
          <div className="flex flex-col md:flex-row items-center justify-between gap-12">
            <div className="flex-1">
              <h2 className="text-3xl font-bold mb-4">Uncorrelated Returns</h2>
              <p className="text-slate-400 leading-relaxed">
                Our ensemble of 6 distinct algorithms dynamically shift capital based on macro volatility regimes. The result is a mathematically smoothed equity curve that actively defends capital during drawdowns.
              </p>
            </div>
            
            <div className="flex-1 grid grid-cols-2 gap-6 w-full">
              <div className="bg-black/50 border border-white/5 p-6 rounded-2xl group-hover:border-blue-500/30 transition-colors duration-500">
                <div className="text-sm text-slate-500 uppercase tracking-wider font-semibold mb-2">Target Annualized</div>
                <div className="text-4xl font-black text-white">42.8%</div>
              </div>
              <div className="bg-black/50 border border-white/5 p-6 rounded-2xl group-hover:border-rose-500/30 transition-colors duration-500">
                <div className="text-sm text-slate-500 uppercase tracking-wider font-semibold mb-2">Max Drawdown</div>
                <div className="text-4xl font-black text-rose-400">-12.4%</div>
              </div>
              <div className="bg-black/50 border border-white/5 p-6 rounded-2xl group-hover:border-emerald-500/30 transition-colors duration-500 col-span-2">
                <div className="text-sm text-slate-500 uppercase tracking-wider font-semibold mb-2">Sharpe Ratio</div>
                <div className="text-4xl font-black text-emerald-400">2.84</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* COMPOSER FEATURE CARDS - The 6 Strategies */}
      <section className="py-32 relative z-10 bg-black/50 border-t border-white/5">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center mb-20">
            <h2 className="text-4xl md:text-5xl font-black mb-6">The Stratos Ecosystem</h2>
            <p className="text-xl text-slate-400 max-w-2xl mx-auto">
              Six highly specialized mathematical modules operating in perfect synchronization.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { id: "Stratos-BP", name: "Base Protocol", desc: "The core foundational trend-following engine evaluating primary market momentum.", color: "from-blue-500 to-indigo-500" },
              { id: "Stratos-AP", name: "Alpha Pursuit", desc: "Aggressive rotational momentum. Rapidly cycles capital into statistically outperforming sectors.", color: "from-indigo-500 to-purple-500" },
              { id: "Stratos-SM", name: "Smart Mean", desc: "Mean-reversion specialist. Identifies over-extended standard deviations and fades the extreme.", color: "from-emerald-500 to-teal-500" },
              { id: "Stratos-PM", name: "Pulse Matrix", desc: "High-frequency volatility harvesting. Captures micro-trends during severe market turbulence.", color: "from-rose-500 to-orange-500" },
              { id: "Stratos-ON", name: "Overnight Node", desc: "Exploits structural overnight risk-premium gaps outside of standard market hours.", color: "from-slate-500 to-slate-300" },
              { id: "Stratos-VX", name: "VIX Hedge", desc: "Pure defensive logic. Automatically dynamically short-sells when macro regime turns hostile.", color: "from-red-600 to-rose-500" }
            ].map((strat) => (
              <div key={strat.id} className="group bg-slate-900/50 backdrop-blur-sm border border-white/5 rounded-3xl p-8 hover:bg-slate-800/50 transition-all duration-500 hover:-translate-y-2 relative overflow-hidden">
                <div className={`absolute top-0 left-0 w-full h-1 bg-gradient-to-r ${strat.color} opacity-50 group-hover:opacity-100 transition-opacity`} />
                <div className="text-sm font-bold text-slate-500 tracking-widest mb-4 uppercase">{strat.id}</div>
                <h3 className="text-2xl font-bold text-white mb-4">{strat.name}</h3>
                <p className="text-slate-400 leading-relaxed">{strat.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FOOTER */}
      <footer className="py-12 border-t border-white/5 text-center text-slate-600">
        <p className="font-medium text-sm">© 2026 Stratos Execution Engine. All rights reserved.</p>
      </footer>
    </main>
  );
}
